import { useRef, useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Play, Pause, ShoppingBag } from "lucide-react";
import { getAlbum, allAlbums } from "@/data/albums";

const CREDITS = [
  { role: "Composition & Production", name: "ØČËĀŃ" },
  { role: "Additional Production", name: "NIKOTHEGREAT" },
  { role: "Mixing", name: "OLYMPUSSTUDIOS" },
  { role: "Mastering", name: "OLYMPUSSTUDIOS" },
  { role: "Artwork & Direction", name: "ØČËĀŃ" },
  { role: "Label", name: "IMMORTAL MULTIMEDIA GROUP" },
];

export default function WorldPage() {
  const { id } = useParams();
  const album = getAlbum(id);
  const heroRef = useRef(null);
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => () => {
    clearInterval(fadeRef.current);
    if (audioRef.current) audioRef.current.pause();
  }, []);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  if (!album) return <Navigate to="/" replace />;

  const hasPreview = Boolean(album.preview);
  const fadeIn = () => {
    const a = audioRef.current;
    if (!a) return;
    clearInterval(fadeRef.current);
    try {
      a.currentTime = 0;
      a.volume = 0;
      a.play().then(() => setPlaying(true)).catch(() => {});
    } catch (e) {}
    fadeRef.current = setInterval(() => {
      if (!a) return;
      a.volume = Math.min(0.85, a.volume + 0.08);
      if (a.volume >= 0.84) clearInterval(fadeRef.current);
    }, 60);
  };
  const fadeOut = () => {
    const a = audioRef.current;
    if (!a) return;
    clearInterval(fadeRef.current);
    fadeRef.current = setInterval(() => {
      if (!a) return;
      a.volume = Math.max(0, a.volume - 0.12);
      if (a.volume <= 0.02) {
        a.pause();
        setPlaying(false);
        clearInterval(fadeRef.current);
      }
    }, 45);
  };
  const togglePreview = () => {
    if (!hasPreview) return;
    if (playing) fadeOut();
    else fadeIn();
  };

  const idx = allAlbums.findIndex((a) => a.id === album.id);
  const prev = allAlbums[(idx - 1 + allAlbums.length) % allAlbums.length];
  const next = allAlbums[(idx + 1) % allAlbums.length];
  const kind = idx < 2 ? "Album" : "Instrumental";

  return (
    <div data-testid={`world-page-${album.id}`} className="relative">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[92svh] w-full overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={album.cover} alt={`${album.title} artwork`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
          <div
            className="absolute inset-0 opacity-40 mix-blend-screen"
            style={{ background: `radial-gradient(70% 60% at 20% 90%, ${album.accent}, transparent 70%)` }}
          />
        </motion.div>

        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-between py-28 md:py-32">
          <Link
            to="/"
            data-testid="world-back"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#c9c6c0] hover:text-white transition-colors w-fit"
          >
            <ArrowLeft size={14} /> The Archive
          </Link>

          <motion.div style={{ y: contentY }}>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10" style={{ background: album.accent }} />
              <span className="font-mono text-[10px] uppercase tracking-[0.35em]" style={{ color: album.accent }}>
                {kind} · {album.tag}
              </span>
            </div>
            <h1 className="font-serif font-light tracking-tight text-[#f2f0ec] text-5xl md:text-8xl leading-[0.88] max-w-5xl overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
              >
                {album.title}
              </motion.span>
            </h1>
            {album.format && album.format.length > 3 && (
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-[#8a8a8a]">{album.format}</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* BODY */}
      <section className="relative bg-[#050505] px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          {/* Lore */}
          <div className="md:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#8a8a8a] mb-10">The World</p>
            <div className="space-y-8">
              {album.lore.map((p, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 26 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
                  className="font-serif text-2xl md:text-3xl leading-[1.45] text-[#e5e2dc]"
                  data-testid={`world-lore-${i}`}
                >
                  {p}
                </motion.p>
              ))}
            </div>

            {/* Listen */}
            <div className="mt-14 flex flex-wrap items-center gap-3">
              {album.stream ? (
                <a
                  href={album.stream}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="world-stream"
                  className="group inline-flex items-center gap-2 border border-white/20 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#f2f0ec] hover:bg-[#f2f0ec] hover:text-black transition-all duration-500"
                >
                  <Play size={12} className="fill-current" /> Stream
                </a>
              ) : (
                <span
                  data-testid="world-stream"
                  title="Streaming link coming soon"
                  className="inline-flex items-center gap-2 border border-white/10 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#555] cursor-not-allowed select-none"
                >
                  <Play size={12} className="fill-current" /> Stream
                </span>
              )}
              <a
                href={album.url}
                target="_blank"
                rel="noreferrer"
                data-testid="world-buy"
                className="group inline-flex items-center gap-2 border border-white/15 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#d8d5ce] hover:text-[#f2f0ec] hover:border-white/40 transition-all duration-500"
              >
                <ShoppingBag size={12} /> Buy
                <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </div>

          {/* Cover + credits */}
          <div className="md:col-span-5">
            <div className="md:sticky md:top-28">
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={togglePreview}
                className={`relative aspect-square w-full overflow-hidden mb-8 ${hasPreview ? "cursor-pointer" : ""}`}
                data-testid="world-cover"
              >
                <motion.img
                  src={album.cover}
                  alt={`${album.title} cover`}
                  className="w-full h-full object-cover"
                  animate={playing ? { scale: [1, 1.035, 1] } : { scale: 1 }}
                  transition={playing ? { duration: 3.6, repeat: Infinity, ease: "easeInOut" } : { duration: 0.6 }}
                />
                <div className="absolute inset-4 border border-white/15 pointer-events-none" />

                <AnimatePresence>
                  {hovered && hasPreview && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <div className="h-16 w-16 rounded-full backdrop-blur-md bg-black/40 border border-white/30 flex items-center justify-center">
                        {playing ? (
                          <Pause size={20} className="fill-white text-white" />
                        ) : (
                          <Play size={20} className="fill-white text-white translate-x-[1px]" />
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {playing && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute left-4 bottom-4 flex items-center gap-3 px-4 py-2 backdrop-blur-md bg-black/50 border border-white/15"
                      data-testid="world-now-playing"
                    >
                      <div className="flex items-end gap-[3px] h-4">
                        {[0, 1, 2, 3, 4].map((i) => (
                          <span
                            key={i}
                            className="w-[3px] rounded-full"
                            style={{ background: album.accent, height: "100%", transformOrigin: "bottom", animation: `eq 0.9s ease-in-out ${i * 0.12}s infinite` }}
                          />
                        ))}
                      </div>
                      <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#f2f0ec]">Playing</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {hasPreview && <audio ref={audioRef} src={album.preview} preload="none" loop />}
              </motion.div>

              {hasPreview && (
                <p className="-mt-4 mb-8 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
                  {playing ? "Playing preview" : "Click cover to play"}
                </p>
              )}

              <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#8a8a8a] mb-5">Credits</p>
              <div className="border-t border-white/10">
                {CREDITS.map((c, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-6 py-3 border-b border-white/10">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a] shrink-0">{c.role}</span>
                    <span className="font-serif text-lg text-[#f2f0ec] text-right">{c.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PREV / NEXT */}
      <section className="border-t border-white/10 grid grid-cols-1 md:grid-cols-2">
        <Link
          to={`/world/${prev.id}`}
          data-testid="world-prev"
          className="group px-6 md:px-12 py-14 hover:bg-[#0b0b0b] transition-colors border-b md:border-b-0 md:border-r border-white/10"
        >
          <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] mb-3">
            <ArrowLeft size={13} /> Previous
          </span>
          <span className="font-serif text-2xl md:text-3xl text-[#f2f0ec] group-hover:opacity-80 transition-opacity">{prev.title}</span>
        </Link>
        <Link
          to={`/world/${next.id}`}
          data-testid="world-next"
          className="group px-6 md:px-12 py-14 hover:bg-[#0b0b0b] transition-colors text-right"
        >
          <span className="flex items-center justify-end gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] mb-3">
            Next <ArrowRight size={13} />
          </span>
          <span className="font-serif text-2xl md:text-3xl text-[#f2f0ec] group-hover:opacity-80 transition-opacity">{next.title}</span>
        </Link>
      </section>
    </div>
  );
}
